import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { MainLayout } from "../layout/MainLayout";
import { AlertCircle, Loader2, UploadCloud, X } from "lucide-react";
import Card from "../common/Card";
import SectionHeader from "../common/SectionHeader";
import FileUploadZone from "../uploads/FileUploadZone";
import PrimaryButton from "../ui/PrimaryButton";
import Toast from "../common/Toast";

const VirtualWindComponent = () => {
  const { projectId } = useParams();

  const [fileData, setFileData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileName, setFileName] = useState("No file chosen");
  const [toast, setToast] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({
    stage: "",
    progress: 0,
  });
  const [processResults, setProcessResults] = useState([]);
  const [showResults, setShowResults] = useState(false);

  const apiRootOperational = process.env.REACT_APP_API_ROOT_OPERATIONAL;
  const token = localStorage.getItem("token");

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  const hideToast = () => {
    setToast(null);
  };

  const handleFilesSelect = (files) => {
    if (files.length === 0) return;
    const file = files[0];

    if (!file.name.endsWith(".zip")) {
      showToast("Please select a ZIP file", "error");
      return;
    }

    setFileName(file.name);
    setFileData(file);
    setShowResults(false);
    setProcessResults([]);
  };

  const uploadToBlob = async (file) => {
    try {
      setUploadProgress({
        stage: "Uploading to storage...",
        progress: 20,
      });

      const formData = new FormData();
      formData.append("file", file);

      const destPath = `projects/${projectId}/wind/${Date.now()}_${file.name}`;
      formData.append("destPath", destPath);
      formData.append("overwrite", "true");

      const uploadResponse = await fetch(
        `${apiRootOperational}/api/files/upload-form`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw new Error(errorData.error || "Failed to upload to blob storage");
      }

      const uploadResult = await uploadResponse.json();

      // Return the path of the uploaded file in blob storage
      return uploadResult.data.path;
    } catch (error) {
      console.error("Error uploading to blob:", error);
      throw error;
    }
  };

  /**
   * Process the uploaded zip file
   */
  const processZipFile = async (blobPath) => {
    try {
      setUploadProgress({
        stage: "Processing wind data...",
        progress: 60,
      });

      const response = await fetch(`${apiRootOperational}/api/process-blob`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          blob_path: blobPath,
          project_id: parseInt(projectId),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to process wind data");
      }

      const result = await response.json();
      setProcessResults(result.results || []);
      return result;
    } catch (error) {
      console.error("Error processing zip file:", error);
      throw error;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!fileData) {
      showToast("Please select a file to upload", "error");
      return;
    }

    if (!projectId) {
      showToast(
        "No project ID found. Please ensure you're in a project context",
        "error"
      );
      return;
    }

    setIsSubmitting(true);
    showToast("Processing upload...", "info");
    setUploadProgress({ stage: "Starting upload process...", progress: 0 });
    setShowResults(false);
    setProcessResults([]);

    try {
      const blobPath = await uploadToBlob(fileData);

      const result = await processZipFile(blobPath);

      setUploadProgress({
        stage: "Processing completed successfully",
        progress: 100,
      });

      showToast(
        `Successfully processed wind data for ${
          result.results?.length || 0
        } directions`,
        "success"
      );
      setShowResults(true);
      setFileData(null);
      setFileName("No file chosen");
    } catch (error) {
      console.error("Error in upload process:", error);
      showToast(`Upload failed: ${error.message}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Render processing results
   */
  const renderResults = () => {
    if (!showResults || processResults.length === 0) return null;

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-6">
        <div className="p-6">
          <SectionHeader title="Processing Results" />

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Direction
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Records
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {processResults.map((result, index) => (
                  <tr
                    key={index}
                    className={
                      result.status === "success" ? "bg-green-50" : "bg-red-50"
                    }
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {result.direction}°
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          result.status === "success"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {result.status === "success" ? "Success" : "Error"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {result.count || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {result.error || "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <MainLayout>
      {/* Toast notification */}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={hideToast} />
      )}

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card
            title={`Virtual Wind Configuration - Project ${projectId}`}
            subtitle="Upload wind direction data for the current project"
          >
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <SectionHeader title="Upload Wind Data" />

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Virtual Wind Data File (ZIP)
                      </label>
                      <FileUploadZone
                        onFilesSelect={handleFilesSelect}
                        disabled={isSubmitting}
                        accept=".zip"
                        acceptedTypes={["ZIP"]}
                      />
                      {fileName !== "No file chosen" && (
                        <p className="mt-2 text-sm text-gray-600">
                          Selected file: {fileName}
                        </p>
                      )}
                    </div>

                    {/* Progress Indicator */}
                    {uploadProgress.stage && (
                      <div className="mb-4">
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div
                            className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                            style={{ width: `${uploadProgress.progress}%` }}
                          ></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-2">
                          {uploadProgress.stage}
                        </p>
                      </div>
                    )}

                    <PrimaryButton
                      onClick={handleSubmit}
                      isLoading={isSubmitting}
                      disabled={!fileData}
                      loadingText="Uploading and Processing..."
                    >
                      Upload Wind Data
                    </PrimaryButton>
                  </div>
                </div>
              </div>

              {renderResults()}

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <SectionHeader title="Upload Requirements" />

                  <div className="bg-blue-50 p-4 rounded-md">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <AlertCircle className="h-5 w-5 text-blue-400" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">
                          Wind Data Format Requirements
                        </h3>
                        <div className="mt-2 text-sm text-blue-700">
                          <ul className="list-disc pl-5 space-y-2">
                            <li>
                              Upload a ZIP file containing your virtual wind
                              data
                            </li>
                            <li>
                              Each JSON file in the ZIP should be named with its
                              wind direction (e.g., 315.json)
                            </li>
                            <li>
                              The system will automatically process and validate
                              the wind data
                            </li>
                            <li>
                              Make sure all JSON files follow the required data
                              format
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default VirtualWindComponent;
